import React, { useState } from 'react';
import { Link, NavLink, useNavigate } from 'react-router-dom';
import { Menu, X, LogOut, UserCircle2, Shield } from 'lucide-react';
import { useAuth } from '../../lib/auth';

const LOGO =
  'https://static.prod-images.emergentagent.com/jobs/8b50f441-6977-4521-a429-f91375c21ad3/images/a7a30377edbe3395688aa9d9533a396000dbf6ce293496a2e88c110296b48027.jpeg';

const links = [
  { to: '/', label: 'Home', testid: 'nav-home' },
  { to: '/buy-ticket', label: 'Buy a Ticket', testid: 'nav-buy' },
  { to: '/faq', label: 'FAQ', testid: 'nav-faq' },
  { to: '/winners', label: 'Previous Winners', testid: 'nav-winners' },
];

export default function Navbar() {
  const [open, setOpen] = useState(false);
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  const handleLogout = () => {
    logout();
    setOpen(false);
    navigate('/');
  };

  return (
    <nav
      data-testid="main-navbar"
      className="fixed top-10 left-0 right-0 z-40 bg-[#0a0a0a]/85 backdrop-blur-xl border-b border-[#D4AF37]/25"
    >
      <div className="w-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-3" data-testid="nav-logo">
          <img src={LOGO} alt="Golden Fish" className="h-11 w-11 rounded-full ring-1 ring-[#D4AF37]/50" />
          <div className="hidden sm:flex flex-col leading-tight">
            <span className="font-serif-display text-lg text-gold-gradient font-black">Golden Fish</span>
            <span className="text-[10px] uppercase tracking-[0.28em] text-[#C0C0C0]/70">Lottery</span>
          </div>
        </Link>

        <div className="hidden md:flex items-center gap-8">
          {links.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              end={l.to === '/'}
              data-testid={l.testid}
              className={({ isActive }) =>
                `text-sm tracking-wide transition-colors ${
                  isActive ? 'text-[#D4AF37]' : 'text-[#C0C0C0] hover:text-[#D4AF37]'
                }`
              }
            >
              {l.label}
            </NavLink>
          ))}
          {user && user.role === 'admin' ? (
            <Link
              to="/admin"
              data-testid="nav-admin"
              className="text-sm text-[#D4AF37] flex items-center gap-1"
            >
              <Shield size={16} /> Admin
            </Link>
          ) : null}
          {user ? (
            <div className="flex items-center gap-4">
              <Link
                to="/dashboard"
                data-testid="nav-dashboard"
                className="text-sm text-[#C0C0C0] hover:text-[#D4AF37] flex items-center gap-1"
              >
                <UserCircle2 size={16} /> Dashboard
              </Link>
              <button
                data-testid="nav-logout"
                onClick={handleLogout}
                className="text-sm text-[#C0C0C0] hover:text-[#D4AF37] flex items-center gap-1"
              >
                <LogOut size={16} /> Logout
              </button>
            </div>
          ) : (
            <div className="flex items-center gap-3">
              <Link to="/login" data-testid="nav-login" className="btn-ghost-gold text-sm !py-2 !px-5">
                Login
              </Link>
              <Link to="/signup" data-testid="nav-signup" className="btn-gold text-sm !py-2 !px-5">
                Sign up
              </Link>
            </div>
          )}
        </div>

        <button
          data-testid="nav-mobile-toggle"
          className="md:hidden text-[#D4AF37] p-2"
          onClick={() => setOpen((s) => !s)}
          aria-label="Toggle menu"
        >
          {open ? <X size={22} /> : <Menu size={22} />}
        </button>
      </div>

      {open && (
        <div data-testid="mobile-menu" className="md:hidden bg-[#0a0a0a] border-t border-[#D4AF37]/20">
          <div className="px-4 py-4 flex flex-col gap-4">
            {links.map((l) => (
              <NavLink
                key={l.to}
                to={l.to}
                end={l.to === '/'}
                onClick={() => setOpen(false)}
                data-testid={`${l.testid}-mobile`}
                className={({ isActive }) =>
                  `text-base ${isActive ? 'text-[#D4AF37]' : 'text-[#C0C0C0]'}`
                }
              >
                {l.label}
              </NavLink>
            ))}
            {user && user.role === 'admin' && (
              <Link to="/admin" data-testid="nav-admin-mobile" onClick={() => setOpen(false)} className="text-[#D4AF37]">
                Admin
              </Link>
            )}
            {user ? (
              <>
                <Link to="/dashboard" data-testid="nav-dashboard-mobile" onClick={() => setOpen(false)} className="text-[#C0C0C0]">
                  Dashboard
                </Link>
                <button data-testid="nav-logout-mobile" className="text-left text-[#C0C0C0]" onClick={handleLogout}>
                  Logout
                </button>
              </>
            ) : (
              <>
                <Link to="/login" data-testid="nav-login-mobile" onClick={() => setOpen(false)} className="text-[#C0C0C0]">
                  Login
                </Link>
                <Link to="/signup" data-testid="nav-signup-mobile" onClick={() => setOpen(false)} className="text-[#D4AF37] font-semibold">
                  Sign up
                </Link>
              </>
            )}
          </div>
        </div>
      )}
    </nav>
  );
}
